(ns minorodata.components.app-admin.forms
  (:require [cljs.spec.alpha :as spec]
            [clojure.set :as set]
            [clojure.string :as str]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.components.forms.password-strength-indicator :as inputs.strength-indicator]
            [minorodata.utils :as utils]
            [mold-forms.core :as form-component]
            [mold-forms.model :refer [->form-model]]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [spec-tools.core :as st]
            [ui.button :refer [button]]
            [ui.dialog :refer [dialog-actions dialog-content
                               dialog-description dialog-root dialog-title]]
            [ui.fields :as fields]
            [ui.typography :as typography]
            [applied-science.js-interop :as js-interop]))


(spec/def ::clientname
  (spec/and (st/spec string? {:reason "Client name should be a string"})
            (st/spec (complement str/blank?) {:reason "Client name can not be empty"})))

(spec/def ::cid
  (spec/and (st/spec string? {:reason "Client id should be a string"})
            (st/spec (complement str/blank?) {:reason "Client id can not be empty"})))

(spec/def ::account-type
  (spec/and (st/spec string? {:reason "Account type should be a string"})
            (st/spec (complement str/blank?) {:reason "Account type can not be empty"})))


(spec/def ::user-name
  (spec/and (st/spec string? {:reason "Username should be a string"})
            (st/spec (complement str/blank?) {:reason "Username can not be empty"})))


;; As per NIST recomendations, only length should be limited. There should not
;; be any limitations on additional password complexity as it doesn't improve
;; security.
;; https://auth0.com/blog/dont-pass-on-the-new-nist-password-guidelines/

(spec/def ::password
  (spec/and (st/spec string? {:reason "Password should be a string"})
            (st/spec (complement str/blank?) {:reason "Password can not be empty"})
            (st/spec #(<= 8 (count %)) {:reason "Password should be at least 8 characters long"})))


(spec/def ::first-name
  (spec/and (st/spec string? {:reason "First name should be a string"})
            (st/spec (complement str/blank?) {:reason "First name can not be empty"})))


(spec/def ::login-name
  (spec/and (st/spec string? {:reason "Login should be a string"})
            (st/spec (complement str/blank?) {:reason "Login can not be empty"})
            (st/spec #(re-matches #"^\S+@\S+\.\S+$" %) {:reason "Login should be an email"})))


(spec/def ::reset-login-name
  (spec/and (st/spec string? {:reason "Login should be a string"})
            (st/spec (complement str/blank?) {:reason "Login can not be empty"})))


(spec/def ::cient-form
  (spec/keys :req-un [::clientname ::account-type ::user-name ::login-name]))

(spec/def ::user-form
  (spec/keys :req-un [::cid ::first-name]))

(spec/def ::password-form
  (spec/keys :req-un [::password ::reset-login-name]))

(spec/def ::client-settings-form
  (spec/keys :req-un [::client-id ::max-warehouse-connections]))


(defn submit-client-form [form-state]
  (let [form-data (form-component/model->fields form-state)]
    (dispatch [:app-admin.api/create-client form-data])))

(defn submit-user-form [form-state]
  (let [form-data (form-component/model->fields form-state)]
    (dispatch [:app-admin.api/create-user form-data])))

(defn submit-password-form [form-state]
  (let [form-data (form-component/model->fields form-state)
        form-data' (set/rename-keys form-data {:reset-login-name :login-name})]
    (dispatch [:app-admin.api/reset-password form-data'])))

(defn client-form-render [{:keys [on-submit errors clientname account-type user-name login-name]}]
  (let [client-types [{:text "Real" :value "client.type/real"}
                      {:text "Trial" :value "client.type/trial"}
                      {:text "Test" :value "client.type/test"}]]
    [:form {:class "flex flex-col gap-2 mt-2" :on-submit on-submit}
     [:div {:class "mb-2 flex flex-col gap-2"}

      [fields/input
       {:field        clientname
        :errors       errors
        :id           :clientname
        :auto-focus   true
        :label        "Client name"}]

      [fields/select
       {:field   account-type
        :errors  errors
        :id      :account-type
        :native  false
        :label   "Account type"
        :options client-types}]]

     [typography/h3 {:class "mt-2"} "Initial admin user"]

     [:div {:class "mb-2 flex flex-col gap-2"}
      [fields/input
       {:field      user-name
        :errors     errors
        :id         :username
        :label      "Username"}]

      [fields/input
       {:field         login-name
        :errors        errors
        :id            :login-name
        :label         "Login (email)"}]]

     [:div {:class "flex justify-end"}
      [button {:type "submit"} "Create new client"]]]))


(defn account-form []
  [:div
   [typography/h2 "Create new client"]

   [form-component/form
    (->form-model
     {:id        :admin-client-form
      :on-submit submit-client-form
      :validate  (partial form-component/validate-form ::client-form)
      :fields    [{:name :clientname :value ""}
                  {:name    :account-type
                   :value   "client.type/real"
                   :process identity}
                  {:name :user-name :value ""}
                  {:name :login-name :value ""}]})
    (fn [fields errors on-submit]
      [client-form-render
       {:on-submit on-submit
        :errors    errors
        :fields    fields

        :clientname   (:clientname fields)
        :account-type (:account-type fields)
        :user-name    (:user-name fields)
        :login-name   (:login-name fields)}])]])


(defonce selected-cid-state (reagent/atom ""))

(def toggle-open? (reagent/atom false))
(def close-toggle #(reset! toggle-open? false))

(defn menu-option [{:keys [clientname cid]}]
  {:value cid :text clientname})

(defn clients-options [clients]
  (let [clients          (sort-by :clientname clients)
        active-clients   (utils/find-all-by-field clients :active true)
        inactive-clients (filter (fn [{:keys [active] :as client}]
                                   (when (false? active) client))
                                 clients)]

    (into [{:type    :group
            :name    "Active clients"
            :options (mapv menu-option active-clients)}
           {:type    :group
            :name    "Inactive clients"
            :options (mapv menu-option inactive-clients)}])))

(def delete-jobs-toggle-open?
  (reagent/atom false))

(def close-delete-jobs-toggle
  #(reset! delete-jobs-toggle-open? false))

(defn client-active? [clients cid]
  (->> clients
       (filter #(= (:cid %) cid))
       first
       :active
       true?))

(defn user-form-render [{:keys [on-submit errors cid first-name clients active-toggle]}]
  [:<>
   [:form {:on-submit on-submit
           :class "flex flex-col gap-2"}

    [:div {:class "flex gap-2"}
     [fields/select
      {:field   (update cid :action
                        (fn [action]
                          (fn [val]
                            (reset! selected-cid-state val)
                            (action val))))
       :errors  errors
       :id      :cid
       :label   "Client name"
       :options (clients-options clients)
       :placeholder "Select client"
       :native  false}]

     [fields/switch
      {:field   (assoc active-toggle :action #(reset! toggle-open? true))
       :errors  errors
       :id      :active-toggle
       :label   "Client active"
       :classes {:root "flex-col-reverse shrink-0 justify-end items-start"}}]]

    [fields/input
     {:field   first-name
      :errors  errors
      :id      :first-name
      :label   "First name"}]

    [:div {:class "mt-20 flex flex-row gap-2 justify-center"}
     [button {:on-click #(reset! delete-jobs-toggle-open? true)
              :disabled (or (nil? @selected-cid-state)
                            (str/blank? @selected-cid-state)
                            (client-active? clients @selected-cid-state))}
      "Delete client jobs"]

     [button {:type "submit"} "Create Kleene admin"]]]

   [dialog-root {:open           @toggle-open?
                 :on-open-change close-toggle}
    [dialog-content
     [dialog-title "Toggle client"]
     [dialog-description
      "Are you sure you want to toggle this client?" [:br]
      [:strong {:class "font-medium"} "Deactivating the client will also deactivate their users."]]
     [dialog-actions
      [button {:on-click close-toggle
               :variant  :secondary}
       "Cancel"]
      [button {:on-click   #(do (close-toggle)
                                (dispatch [:app-admin.api/toggle-client
                                           {:cid    @selected-cid-state
                                            :active (not (true? (:value active-toggle)))}]))
               :auto-focus true}
       "Toggle client"]]]]

   [dialog-root {:open           @delete-jobs-toggle-open?
                 :on-open-change close-delete-jobs-toggle}
    [dialog-content
     [dialog-title "Delete client jobs"]
     [dialog-description
      "Are you sure you want to delete this client's jobs?" [:br]
      [:strong {:class "font-medium"} "All client's extracts and transforms will be deleted"]]
     [dialog-actions
      [button {:on-click close-delete-jobs-toggle
               :variant  :secondary}
       "Cancel"]
      [button {:on-click   #(do (close-delete-jobs-toggle)
                                (dispatch [:app-admin.api/delete-client-jobs
                                           {:client-id @selected-cid-state}]))
               :auto-focus true}
       "Delete jobs"]]]]])

(defn submit-client-settings-form [form-state]
  (let [form-data (form-component/model->fields form-state)]
    (dispatch [:app-admin.api/push-tenant-config form-data])))

(defn client-configs-form-render
  [{:keys [on-submit client-configs errors fields client-id]}]

  [:form {:on-submit on-submit
          :class     "flex flex-col gap-2"}

   [:div {:class "mt-2 flex flex-row gap-2 items-end"}
    [fields/input
     {:field  (:max-warehouse-connections fields)
      :errors errors
      :id     :max-warehouse-connections
      :label  "Maximum Warehouse Connections"}]
    [button {:type "submit"} "Save"]]])

(defn client-config-form [{:keys [client-configs]}]
  [:div
   [typography/h2 {:class "mb-4 mt-5"} "Configuration"]

   [:div {:class "my-4"}
    [typography/p
     "This will define the maximum number of transforms that can be executed concurrently."]]
   [form-component/form
    (->form-model
      {:id        :admin-kleene-client-config-form
       :on-submit submit-client-settings-form
       :validate  (partial form-component/validate-form ::client-settings-form)
       :fields    [{:name    :client-id
                    :value   @selected-cid-state}
                   {:name  :max-warehouse-connections
                    :value (when (not-empty @selected-cid-state)
                             (-> (utils/find-by-field client-configs :client-id @selected-cid-state)
                                 :max-warehouse-connections))}]})
    (fn [fields errors on-submit]
      [client-configs-form-render
       {:on-submit     on-submit
        :client-configs client-configs
        :errors        errors
        :fields        fields
        :client-id     (:client-id fields)}])]])

(defn add-kleene-user-form [{:keys [clients client-configs]}]
  [:div
   [typography/h2 {:class "mb-4"} "Add admin user"]

   [:div {:class "my-4"}
    [typography/p
     "This will create a new kleene admin user in selected client’s account."]
    [typography/p
     "If it fails bacause there already exists a kleene admin user and you don’t remember the password for it, use password reset form to set a new password."]]
   [form-component/form
    (->form-model
     {:id        :admin-kleene-user-form
      :on-submit submit-user-form
      :validate  (partial form-component/validate-form ::user-form)
      :fields    [{:name    :cid
                   :value   @selected-cid-state}
                  {:name  :first-name
                   :value ""}
                  {:name  :active-toggle
                   :value (if (not-empty @selected-cid-state)
                            (-> (utils/find-by-field clients :cid @selected-cid-state)
                                :active)
                            false)}]})
    (fn [fields errors on-submit]
      [user-form-render
       {:on-submit     on-submit
        :clients       clients
        :errors        errors
        :fields        fields
        :cid           (:cid fields)
        :active-toggle (:active-toggle fields)
        :first-name    (:first-name fields)}])]
   [client-config-form {:client-configs client-configs}]])

(defn old-pwd-indicator [password]
  [:<>
   [inputs.strength-indicator/indicator
    {:field      password
     :min-length 8}]
   [:p
    "Make sure the password is at least 8 characters long."
    [:br]
    "A good password should include special characters, numbers and a mix of uppercase and lowercase letters."]])

(defn new-pwd-indicator [password]
  [:<>
   [inputs.strength-indicator/hacky-indicator
    {:field password}]

   [typography/p
    "The password must be at least 12 characters long and must include at least one uppercase letter, one lowercase letter, one digit and one special character."]])


(defn reset-password-form-render [{:keys [on-submit errors password login-name]}]
  [:form {:class     "flex flex-col gap-2"
          :on-submit on-submit}

   [fields/input
    {:field         login-name
     :errors        errors
     :id            :login-name
     :label         "Login (email)"
     :auto-complete "email"}]

   [fields/input
    {:field         password
     :errors        errors
     :id            :password
     :label         "New password"
     :type          "password"
     :auto-complete "email"}]

   [new-pwd-indicator password]

   [:div {:class "flex justify-end"}
    [button {:type "submit"} "Reset password"]]])


(defn reset-password-form []
  [form-component/form
   (->form-model
    {:id        :admin-password-form
     :on-submit submit-password-form
     :validate  (partial form-component/validate-form ::password-form)
     :fields    [{:name :password :value ""}
                 {:name :reset-login-name :value ""}]})
   (fn [fields errors on-submit]
     [reset-password-form-render
      {:on-submit  on-submit
       :errors     errors
       :fields     fields
       :password   (:password fields)
       :login-name (:reset-login-name fields)}])])
